import BPromise from 'bluebird';
import React, { useRef, useEffect } from 'react';
import firebase from 'firebase/app';
import 'firebase/database';
import firebaseConfig from './firebase-config';

export type ChatMessage = {
  text: string;
  vid: string; // viewer_id
  date?: string; // ISO timestamp
  hide?: boolean;
};

export type DBMessage = {
  key: string;
  val: ChatMessage;
};

export type ChatAuthor = {
  name: string;
  hide?: boolean;
  mod?: boolean;
};

export type AuthorList = {
  [viewerID: string]: ChatAuthor;
};

let firebaseAppMap = {};
let recentMessageTimestampsStack = [];

function loadFirebase(dbName: string, firebaseConfig: Object): Promise<firebase.app.App> {
  if (!firebaseAppMap[dbName]) {
    const config = { ...firebaseConfig, databaseURL: `https://${dbName}.firebaseio.com` };
    firebaseAppMap[dbName] = firebase.initializeApp(config, dbName);
  }
  return BPromise.resolve(firebaseAppMap[dbName]);
}

function pathForBroadcast(broadcast: any): string {
  const yearMonth = broadcast.starts_at.substr(0, 7);
  return `/chats/${yearMonth}/${broadcast.account_id}/${broadcast.id}`;
}

function chatDBMessageDictionaryToArray(data: firebase.database.DataSnapshot): DBMessage[] {
  // Transform Firebase data into a flat array of messages
  const msgDict = data.val() as ChatMessage;
  return Object.keys(msgDict || {}).map((key) => {
    return { key: key, val: msgDict[key] };
  });
}

// The `useChatDataBase` hook is used to get the chat data from the database.
// @param {object} broadcast - The current broadcast
// @param {function} onMessageFromFirebase - The callback function to call when the list of chat messages are updated.
export default function useChatDatabase(options: {
  dbName: string;
  broadcast: any;
  onMessageFromFirebase: (messages: DBMessage[]) => void;
  onAuthorListUpdated: (authorList: AuthorList) => void;
}): [
  React.MutableRefObject<firebase.database.Reference>,
  (msg: ChatMessage, authorName: string) => void
] {
  const { dbName, broadcast, onMessageFromFirebase, onAuthorListUpdated } = options;
  const database = useRef<firebase.database.Reference>(null);

  const wrappedOnMessageFromFirebase = (data: firebase.database.DataSnapshot) => {
    onMessageFromFirebase(chatDBMessageDictionaryToArray(data));
  };

  let authorList: AuthorList = {};
  const wrappedOnAuthorListUpdated = (data: firebase.database.DataSnapshot) => {
    authorList = data.val();
    if (!authorList) authorList = {};
    onAuthorListUpdated(authorList);
  };

  useEffect(() => {
    let messageQuery: firebase.database.Query | null = null;
    let authorListQuery: firebase.database.Query | null = null;

    (async () => {
      if (!dbName) return;
      const firebaseApp = await loadFirebase(dbName, firebaseConfig);
      const db = firebase.database(firebaseApp).ref(pathForBroadcast(broadcast));
      database.current = db;
      // TODO: pagination and virtual scroll support
      messageQuery = db.child('messages').orderByChild('hide').equalTo(null).limitToLast(200);
      messageQuery.on('value', wrappedOnMessageFromFirebase);
      authorListQuery = db.child('authors');
      authorListQuery.on('value', wrappedOnAuthorListUpdated);
    })();

    return () => {
      messageQuery && messageQuery.off('value', wrappedOnMessageFromFirebase);
      authorListQuery && authorListQuery.off('value', wrappedOnAuthorListUpdated);
    };
  }, [broadcast.id, dbName]);

  const sendMessage = (msg: ChatMessage, authorName: string) => {
    if (!database.current) {
      throw new Error('Chat is not connected');
    }
    // First, check that the author is registered and updated
    if (authorList[msg.vid]?.name != authorName) {
      // Use `update` rather than `set` to avoid clobbering shadow ban status
      database.current.child('authors').child(msg.vid).update({ name: authorName });
    }
    // Client-side rate limiting
    if (recentMessageTimestampsStack.length > 4) {
      const oldest = recentMessageTimestampsStack[0];
      const secondsSince5MessagesAgo = (Date.now() - oldest) / 1000;
      if (secondsSince5MessagesAgo < 30) {
        const timeRemainingToSendMessage = Math.ceil(30 - secondsSince5MessagesAgo);
        throw new Error(`rate limit ${timeRemainingToSendMessage}`);
      }
      recentMessageTimestampsStack.shift();
    }
    // Now send message
    recentMessageTimestampsStack.push(Date.now());
    database.current
      .child('messages')
      .push()
      .set({
        ...msg,
        date: new Date().toISOString(),
      });
  };

  return [database, sendMessage];
}
